import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router'
import { trackEvent } from '@penn/web-common'
import { LoginPage } from './pages/auth/LoginPage'
import { RegisterPage } from './pages/auth/RegisterPage'
import { PlatformRegisterRedirect } from './pages/auth/PlatformRegisterRedirect'
import { CallbackPage } from './pages/auth/CallbackPage'
import { NavBar } from './components/constant/NavBar'
import { SubNavBar } from './components/constant/SubNavBar'
import { SupportAssistant } from './components/constant/SupportAssistant'
import { ProtectedRoute, GuestRoute } from '@penn/web-common'
import { WorkForOpenPage } from './pages/work-for/WorkForOpenPage'
import { LandingPage } from './pages/landing-page/LandingPage'
import { FilterPage } from './pages/filter-page/FilterPage'
import { StepCheckPage } from './pages/cad/StepCheckPage'
import { ComparePage } from './pages/cad/ComparePage'
import { NotFoundPage } from './pages/NotFoundPage'
import { useAppDispatch } from './stores/hooks'
import { setLogout } from '@penn/web-common'
import { subscribeAuthSync } from '@penn/web-common'
import { dispatchCompareCostInvalidate } from '@penn/web-common'
import { cognitoSignOut } from '@penn/web-common'
import { OIDC_ENABLED } from '@penn/web-common'
import { stopSessionRefresh } from '@penn/web-common'
import { SessionBoot } from '@penn/web-common'

function AppLayout() {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const hideHeader = location.pathname === '/cad/compare'

  useEffect(() => {
    trackEvent('page_view', { title: document.title })
  }, [location.pathname])

  // Cross-tab logout. Only a DELIBERATE logout broadcasts now (ADR-0036) — an
  // unrecoverable 401 no longer does, because one stale background tab used to
  // evict every other tab, valid sessions included.
  useEffect(() => {
    return subscribeAuthSync(() => {
      stopSessionRefresh()
      void cognitoSignOut()
      dispatch(setLogout())
      // Re-read the (now cleared) Work-For so the banner clears in this tab too.
      dispatchCompareCostInvalidate()
      // No state: in AUTH_MODE=oidc LoginPage only auto-starts beginLogin() on
      // an intent-carrying navigation, so a bare arrival here must NOT race the
      // still-live issuer SSO cookie to /authorize while /logout is in flight.
      navigate('/login', { replace: true, state: null })
    })
  }, [dispatch, navigate])

  return (
    <>
      {!hideHeader && (
        <>
          <SubNavBar />
          <NavBar />
        </>
      )}
      <SupportAssistant />
      <Routes>


        {/* Guest-only routes — redirect to /home when already logged in */}
        <Route path="/login" element={<GuestRoute><LoginPage /></GuestRoute>} />
        {/* OIDC redirect target (AUTH_MODE=oidc). Registered in every build so the
            client's redirect URI is stable; inert (bounces to /login) otherwise. */}
        <Route path="/auth/callback" element={<CallbackPage />} />
        {/* AUTH_MODE=oidc: no in-app sign-up form; hand off to the platform sign-up door. */}
        <Route
          path="/register"
          element={OIDC_ENABLED ? <PlatformRegisterRedirect /> : <GuestRoute><RegisterPage /></GuestRoute>}
        />
        {/* Public: the landing page is the anonymous entry point, which is why
            signing out returns here rather than to /login. */}
        <Route path="/" element={<LandingPage />} />

        {/* Protected routes — redirect to /login when not authenticated */}
        {/* Public — anonymous visitors can browse/filter; creating or opening
            an application is gated inside FilterPage via requireAuth. */}
        <Route path="/installation-system" element={<FilterPage />} />
        {/* Reachable only via the Work-For "Apply" button — the page itself
            bounces direct visits without the location-state flag. */}
        {/* Cross-origin Work-For handoff from the internal band's new-tab link.
            NOT behind ProtectedRoute: a fresh tab on this origin never has a
            session (no cookie in dev, and storage does not cross origins), and
            ProtectedRoute bounces to /login with no intent, which renders the
            passive Sign in button. The page bounces with intent itself. */}
        <Route path="/work-for/open" element={<WorkForOpenPage />} />
        <Route path="/cad/stepcheck" element={<ProtectedRoute><StepCheckPage /></ProtectedRoute>} />
        <Route path="/cad/compare" element={<ProtectedRoute><ComparePage /></ProtectedRoute>} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </> 
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <SessionBoot>
        <AppLayout />
      </SessionBoot>
    </BrowserRouter>
  )
}
